/**
 * Chat Engine — core LLM loop with tool execution and routing.
 * Framework-agnostic. All external deps injected.
 *
 * Message contract: callers always pass raw display-track messages
 * (role: 'user' | 'participant'). The engine converts to LLM format
 * via conversationToPrompt before sending to the LLM.
 */

import { parseMention } from './parse-mention.js'

function formatToolResult(result) {
  if (result.success) {
    if (result.result === undefined || result.result === null) {
      return JSON.stringify({ result: result.result, output: result.output || null })
    }
    return JSON.stringify(result.result)
  }
  return JSON.stringify({ error: result.error || 'Unknown error' })
}

async function executeAllTools(toolCalls, executeTool) {
  const results = await Promise.all(toolCalls.map(async (tc) => {
    const result = await executeTool(tc)
    return {
      toolResult: { toolCallId: tc.id, toolName: tc.function.name, content: formatToolResult(result) },
      innerToolCalls: result.innerToolCalls || [],
    }
  }))
  return {
    toolResults: results.map(r => r.toolResult),
    allInnerToolCalls: results.flatMap(r => r.innerToolCalls),
  }
}

/**
 * Create a chat engine.
 * @param {Function} chatFn - (messages, botConfig?) => { status, answer, toolCalls, usage, modelInfo }
 * @param {Function} executeTool - (toolCall) => { success, result, error, innerToolCalls }
 * @param {Function} buildPrompt - (botConfig, participants?) => systemPromptString
 * @param {Function} conversationToPrompt - (messages, selfId, nameMap) => [{role, content}]
 * @param {Function} roleCall - (roleId, method, params) => result
 * @param {string} [hostId] - Host bot's participant ID (for main chat context conversion)
 * @param {number} [maxIterations=10]
 */
export function createChatEngine({ chatFn, executeTool, buildPrompt, conversationToPrompt, roleCall, hostId, maxIterations: defaultMax = 10 }) {

  async function runLlmLoop(messages, { botConfig, onStep, onToolCall, onAsyncTask, maxIterations }) {
    let currentMessages = [...messages]
    let iteration = 0
    const toolInteractions = []
    const max = maxIterations || defaultMax

    while (iteration < max) {
      iteration++
      onStep?.('calling_llm')
      const response = await chatFn(currentMessages, botConfig)

      if (response.status === 'complete') {
        return { answer: response.answer, usage: response.usage, modelInfo: response.modelInfo, toolInteractions }
      }
      if (response.status !== 'needs_tool_call') throw new Error(`Unexpected status: ${response.status}`)

      onStep?.('executing_tools')
      const { toolResults, allInnerToolCalls } = await executeAllTools(response.toolCalls, executeTool)
      toolInteractions.push({ answer: response.answer, toolCalls: response.toolCalls, toolResults, innerToolCalls: allInnerToolCalls })

      for (const tc of allInnerToolCalls) {
        if (tc.source === 'role') onToolCall?.(tc)
        if (tc.source === 'role' && tc.result?.taskId) onAsyncTask?.({ taskId: tc.result.taskId, roleId: tc.roleId })
      }

      currentMessages = [
        ...currentMessages,
        { role: 'assistant', content: response.answer || null, toolCalls: response.toolCalls },
        ...toolResults.map(r => ({ role: 'tool', toolCallId: r.toolCallId, content: r.content })),
      ]
    }
    throw new Error(`Max iterations (${max}) reached`)
  }

  /**
   * Route to a remote worker via RPC.
   */
  async function callWorker(roleId, displayName, message, messages) {
    const nameMap = { [roleId]: displayName }
    const context = conversationToPrompt(messages, roleId, nameMap)
    const result = await roleCall(roleId, 'chat', [{ message, context }])
    if (result?.taskId) return { type: 'async', taskId: result.taskId, responderId: roleId, responderName: displayName }
    return { type: 'response', answer: result?.response, responderId: roleId, responderName: displayName }
  }

  /**
   * Unified entry point.
   *
   * @param {string} content - User message
   * @param {Object} [options]
   * @param {Object} [options.target] - Conversation target { roleId, type, displayName, botConfig? }. null = host LLM.
   * @param {Object} [options.botConfig] - LLM config (used when target is null or target.botConfig is absent)
   * @param {Array} [options.participants] - For @mention routing + system prompt (main chat only)
   * @param {Array} [options.messages] - Conversation history (raw display-track messages)
   * @param {Function} [options.onStep] - ('calling_llm'|'executing_tools') => void
   * @param {Function} [options.onToolCall] - (innerToolCall) => void
   * @param {Function} [options.onAsyncTask] - ({ taskId, roleId }) => void
   * @param {number} [options.maxIterations]
   *
   * @returns {{ type: 'response'|'async', answer?, usage?, modelInfo?, durationMs?,
   *             toolInteractions?, responderId?, responderName?, taskId? }}
   */
  const sendMessage = async (content, options = {}) => {
    const { target, botConfig, participants = [], messages = [],
            onStep, onToolCall, onAsyncTask, maxIterations } = options

    // --- 1. @mention routing (main chat only, when no explicit target) ---
    let llmContent = content
    if (!target && participants.length > 0) {
      const mention = parseMention(content, participants)
      const mentionTarget = mention ? participants.find(p => p.roleId === mention.targetRoleId) : null

      if (mentionTarget?.roleType === 'host') {
        llmContent = mention.messageBody || content
      } else if (mention?.messageBody && mentionTarget?.roleType === 'chat') {
        return callWorker(mention.targetRoleId, mentionTarget.displayName, mention.messageBody, messages)
      }
    }

    // --- 2. Worker RPC (non-LLM target) ---
    if (target && target.type !== 'llm-bot') {
      return callWorker(target.roleId, target.displayName, content, messages)
    }

    // --- 3. LLM loop (host or LLM bot — unified) ---
    const bc = target ? (target.botConfig || botConfig) : botConfig
    const systemPrompt = buildPrompt(bc, target ? undefined : participants)

    const selfId = target ? target.roleId : hostId
    const nameMap = target ? { [target.roleId]: target.displayName } : {}
    const context = conversationToPrompt(messages, selfId, nameMap)
    const llmMessages = [
      { role: 'system', content: systemPrompt },
      ...context,
      { role: 'user', content: target ? content : llmContent },
    ]

    const t0 = Date.now()
    const result = await runLlmLoop(llmMessages, { botConfig: bc, onStep, onToolCall, onAsyncTask, maxIterations })
    return { type: 'response', ...result, responderId: target?.roleId, responderName: target?.displayName, durationMs: Date.now() - t0 }
  }

  return { sendMessage }
}
